import classNames from 'classnames'
import moment from 'moment'
import * as React from 'react'
import { Redirect } from 'react-router'
import { Column, useTable, useSortBy, usePagination } from 'react-table'
import ReactTablePagination from 'src/components/ui/ReactTablePagination'
import { IRiderChangeLogDTO } from 'src/types'
import { ILogEntryResponseDTO } from 'src/types/log-entry'
import ArrowRightIcon from '../../icons/ui/ArrowRight'
import FloatingForm from 'src/components/ui/form/FloatingForm'
import { MoonLoader } from 'react-spinners'

interface IRiderChangeLogProps {
  riderId: string
  riderChangeLog: ILogEntryResponseDTO[]
  riderChangeLogCount: number
  getRiderChangeLog: (params: IRiderChangeLogDTO) => Promise<any>
}

interface IProps<T extends object> {
  columns: Array<Column<T>>
  data: T[]
  loading: boolean
  pageCount: number
  fetchData: (params: { pageIndex: number; pageSize: number }) => void
}

const PAGE_SIZE = 10

function Table<T extends object>({
  columns,
  data,
  fetchData,
  loading,
  // filters,
  pageCount: controlledPageCount
}: IProps<T>) {
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    page,
    prepareRow,
    canPreviousPage,
    canNextPage,
    nextPage,
    previousPage,
    setPageSize,
    state
  } = useTable<T>(
    {
      columns,
      data,
      initialState: {
        pageIndex: 0,
        pageSize: PAGE_SIZE
      },
      manualSortBy: true,
      manualPagination: true,
      pageCount: controlledPageCount
    },
    useSortBy,
    usePagination
  )

  const { pageIndex, pageSize, sortBy } = state

  React.useEffect(() => {
    console.log(pageSize, pageIndex)
    fetchData({ pageIndex, pageSize })
  }, [pageIndex, pageSize])

  React.useEffect(() => {
    console.log(pageSize, pageIndex)
    const sortParams = sortBy.map(s => {
      return s.id + '-' + (s.desc ? 'desc' : 'asc')
    })
    fetchData({ pageIndex, pageSize })
  }, [sortBy])

  return (
    <React.Fragment>
      {rows.length > 0 && (
        <table {...getTableProps()}>
          <thead>
            {headerGroups.map(headerGroup => (
              <tr {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map(column => (
                  <th
                    className={classNames('', {
                      sortable: column.canSort,
                      sortedAsc: column.isSortedDesc === false,
                      sortedDesc: column.isSortedDesc === true
                    })}
                    {...column.getHeaderProps(column.getSortByToggleProps())}
                    style={{
                      width: column.width
                    }}
                  >
                    {column.render('Header')}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody {...getTableBodyProps()}>
            {loading ? (
              <tr>
                <td>Loading...</td>
              </tr>
            ) : (
              rows.map(
                (row, i) =>
                  +prepareRow(row) || (
                    <tr {...row.getRowProps()}>
                      {row.cells.map((cell, index) => {
                        return (
                          <td {...cell.getCellProps()} key={`1-${index}`}>
                            {cell.render('Cell')}
                          </td>
                        )
                      })}
                    </tr>
                  )
              )
            )}
          </tbody>
        </table>
      )}
      {rows.length === 0 && (
        <div className='p2 flex items-center justify-center h5 font-grey2'>
          <img width={130} src='/images/no_data.gif' alt='No data availabe.' />
        </div>
      )}
      <ReactTablePagination
        page={pageIndex}
        pages={controlledPageCount}
        pageSize={pageSize}
        canNext={canNextPage}
        canPrevious={canPreviousPage}
        nextPage={nextPage}
        previousPage={previousPage}
        rowCount={rows.length}
      />
    </React.Fragment>
  )
}

export const RiderChangeLog = (props: IRiderChangeLogProps) => {
  const [loading, setLoading] = React.useState(true)
  const [riderChangeLog, setRiderChangeLog] = React.useState<any[]>([])
  const [riderChangeLogCount, setRiderChangeLogCount] = React.useState<number>(0)
  const columns = React.useMemo(
    () => [
      {
        id: 'createdAt',
        Header: 'Created At',
        width: '12%',
        accessor: (logEntry: ILogEntryResponseDTO) => {
          return <div>{moment(logEntry.createdAt).format('YYYY-MM-DD hh:mmA')}</div>
        }
      },
      {
        id: 'action',
        Header: 'Action',
        width: '15%',
        accessor: (logEntry: ILogEntryResponseDTO) => {
          return <div>{getFieldName(logEntry.action)}</div>
        }
      },
      {
        id: 'user',
        Header: 'User',
        width: '13%',
        accessor: (logEntry: ILogEntryResponseDTO) => {
          return (
            <div>
              {logEntry.userDetails.firstName} {logEntry.userDetails.lastName}
            </div>
          )
        }
      },
      {
        id: 'email',
        Header: 'Email',
        width: '12%',
        accessor: (logEntry: ILogEntryResponseDTO) => {
          return <div>{logEntry.userDetails.email}</div>
        }
      },
      {
        id: 'message',
        Header: 'Changes',
        width: '48%',
        className: 'flex',
        accessor: (logEntry: ILogEntryResponseDTO) => {
          const objMessage = logEntry.message ? JSON.parse(logEntry.message) : ''
          let updateAtDisplay = ''
          let temp: any[]
          return (
            <div className='flex'>
              {Object.keys(objMessage).map(key => {
                if (key === 'data') {
                  temp = Object.keys(objMessage[key]).map((field, index) => {
                    if (field === 'birthDate') {
                      if (objMessage[key][field].birthDate !== undefined) {
                        const tempOldBirthDate: string = objMessage[key][field].birthDate.__old
                        const tempNewBirthDate: string = objMessage[key][field].birthDate.__new
                        const isValidDate = (date: string | null) => date && date.includes('T')

                        const tempOldBirthDateArray: string[] = isValidDate(tempOldBirthDate)
                          ? tempOldBirthDate.split('T')
                          : ['(empty)']
                        const tempNewBirthDateArray: string[] = isValidDate(tempNewBirthDate)
                          ? tempNewBirthDate.split('T')
                          : ['(empty)']

                        return (
                          <div className='flex flex-row h5 w100'>
                            <div className='flex items-center justify-center mr2'>
                              <span className='h5 mr2 bold font-black'>{getFieldName(field)}: </span>
                              {tempOldBirthDateArray[0]}{' '}
                            </div>
                            <div className='flex items-center justify-center mr2'>
                              <img
                                aria-label='Changed to'
                                src={'/images/arrow.png'}
                                style={{
                                  height: '16px',
                                  width: '16px',
                                  objectFit: 'cover',
                                  padding: '0px',
                                  alignSelf: 'center'
                                }}
                              />
                            </div>
                            <div className='flex items-center justify-center'>{tempNewBirthDateArray[0]}</div>
                          </div>
                        )
                      } else {
                        const tempOldBirthDate: string = objMessage[key][field].__old
                        const tempNewBirthDate: string = objMessage[key][field].__new
                        const isValidDate = (date: string | null) => date && date.includes('T')

                        const tempOldBirthDateArray: string[] = isValidDate(tempOldBirthDate)
                          ? tempOldBirthDate.split('T')
                          : ['(empty)']
                        const tempNewBirthDateArray: string[] = isValidDate(tempNewBirthDate)
                          ? tempNewBirthDate.split('T')
                          : ['(empty)']

                        return (
                          <div className='flex flex-row h5 w100'>
                            <div className='flex items-center justify-center mr2'>
                              <span className='h5 mr2 bold font-black'>{getFieldName(field)}: </span>
                              {tempOldBirthDateArray[0]}{' '}
                            </div>
                            <div className='flex items-center justify-center mr2'>
                              <img
                                aria-label='Changed to'
                                src={'/images/arrow.png'}
                                style={{
                                  height: '16px',
                                  width: '16px',
                                  objectFit: 'cover',
                                  padding: '0px',
                                  alignSelf: 'center'
                                }}
                              />
                            </div>
                            <div className='flex items-center justify-center'>{tempNewBirthDateArray[0]}</div>
                          </div>
                        )
                      }
                    } else if (field === 'email') {
                      if (objMessage[key][field].emailAddress !== undefined) {
                        return (
                          <div className='flex flex-row h5 w100'>
                            <div className='flex items-center justify-center mr2'>
                              <span className='h5 mr2 bold font-black'>{getFieldName(field)}: </span>
                              {objMessage[key][field].emailAddress.__old !== null
                                ? objMessage[key][field].emailAddress.__old
                                : '(empty)'}{' '}
                            </div>
                            <div className='flex items-center justify-center mr2'>
                              <img
                                aria-label='Changed to'
                                src={'/images/arrow.png'}
                                style={{
                                  height: '16px',
                                  width: '16px',
                                  objectFit: 'cover',
                                  padding: '0px',
                                  alignSelf: 'center'
                                }}
                              />
                            </div>
                            <div className='flex items-center justify-center'>
                              {objMessage[key][field].emailAddress.__new !== null
                                ? objMessage[key][field].emailAddress.__new
                                : '(empty)'}
                            </div>
                          </div>
                        )
                      } else {
                        return (
                          <div className='flex flex-row h5 w100'>
                            <div className='flex items-center justify-center mr2'>
                              <span className='h5 mr2 bold font-black'>{getFieldName(field)}: </span>
                              {objMessage[key][field].__old !== null
                                ? objMessage[key][field].__old.emailAddress
                                : '(empty)'}{' '}
                            </div>
                            <div className='flex items-center justify-center mr2'>
                              <img
                                aria-label='Changed to'
                                src={'/images/arrow.png'}
                                style={{
                                  height: '16px',
                                  width: '16px',
                                  objectFit: 'cover',
                                  padding: '0px',
                                  alignSelf: 'center'
                                }}
                              />
                            </div>
                            <div className='flex items-center justify-center'>
                              {objMessage[key][field].__new !== null
                                ? objMessage[key][field].__new.emailAddress
                                : '(empty)'}
                            </div>
                          </div>
                        )
                      }
                    } else if (field === 'firstName') {
                      if (objMessage[key][field].firstName !== undefined) {
                        return (
                          <div className='flex flex-row h5 w100'>
                            <div className='flex items-center justify-center mr2'>
                              <span className='h5 mr2 bold font-black'>{getFieldName(field)}: </span>
                              {objMessage[key][field].firstName.__old !== null
                                ? objMessage[key][field].firstName.__old
                                : '(empty)'}{' '}
                            </div>
                            <div className='flex items-center justify-center mr2'>
                              <img
                                aria-label='Changed to'
                                src={'/images/arrow.png'}
                                style={{
                                  height: '16px',
                                  width: '16px',
                                  objectFit: 'cover',
                                  padding: '0px',
                                  alignSelf: 'center'
                                }}
                              />
                            </div>
                            <div className='flex items-center justify-center'>
                              {objMessage[key][field].firstName.__new !== null
                                ? objMessage[key][field].firstName.__new
                                : '(empty)'}
                            </div>
                          </div>
                        )
                      } else {
                        return (
                          <div className='flex flex-row h5 w100'>
                            <div className='flex items-center justify-center mr2'>
                              <span className='h5 mr2 bold font-black'>{getFieldName(field)}: </span>
                              {objMessage[key][field].__old !== null ? objMessage[key][field].__old : '(empty)'}{' '}
                            </div>
                            <div className='flex items-center justify-center mr2'>
                              <img
                                aria-label='Changed to'
                                src={'/images/arrow.png'}
                                style={{
                                  height: '16px',
                                  width: '16px',
                                  objectFit: 'cover',
                                  padding: '0px',
                                  alignSelf: 'center'
                                }}
                              />
                            </div>
                            <div className='flex items-center justify-center'>
                              {objMessage[key][field].__new !== null ? objMessage[key][field].__new : '(empty)'}
                            </div>
                          </div>
                        )
                      }
                    } else if (field === 'lastName') {
                      if (objMessage[key][field].lastName !== undefined) {
                        return (
                          <div className='flex flex-row h5 w100'>
                            <div className='flex items-center justify-center mr2'>
                              <span className='h5 mr2 bold font-black'>{getFieldName(field)}: </span>
                              {objMessage[key][field].lastName.__old !== null
                                ? objMessage[key][field].lastName.__old
                                : '(empty)'}{' '}
                            </div>
                            <div className='flex items-center justify-center mr2'>
                              <img
                                aria-label='Changed to'
                                src={'/images/arrow.png'}
                                style={{
                                  height: '16px',
                                  width: '16px',
                                  objectFit: 'cover',
                                  padding: '0px',
                                  alignSelf: 'center'
                                }}
                              />
                            </div>
                            <div className='flex items-center justify-center'>
                              {objMessage[key][field].lastName.__new !== null
                                ? objMessage[key][field].lastName.__new
                                : '(empty)'}
                            </div>
                          </div>
                        )
                      } else {
                        return (
                          <div className='flex flex-row h5 w100'>
                            <div className='flex items-center justify-center mr2'>
                              <span className='h5 mr2 bold font-black'>{getFieldName(field)}: </span>
                              {objMessage[key][field].__old !== null ? objMessage[key][field].__old : '(empty)'}{' '}
                            </div>
                            <div className='flex items-center justify-center mr2'>
                              <img
                                aria-label='Changed to'
                                src={'/images/arrow.png'}
                                style={{
                                  height: '16px',
                                  width: '16px',
                                  objectFit: 'cover',
                                  padding: '0px',
                                  alignSelf: 'center'
                                }}
                              />
                            </div>
                            <div className='flex items-center justify-center'>
                              {objMessage[key][field].__new !== null ? objMessage[key][field].__new : '(empty)'}
                            </div>
                          </div>
                        )
                      }
                    } else if (field === 'note') {
                      if (objMessage[key][field].note !== undefined) {
                        return (
                          <div className='flex flex-row h5 w100'>
                            <div className='flex items-center justify-center mr2'>
                              <span className='h5 mr2 bold font-black'>{getFieldName(field)}: </span>
                              {objMessage[key][field].note.__old !== null
                                ? objMessage[key][field].note.__old
                                : '(empty)'}{' '}
                            </div>
                            <div className='flex items-center justify-center mr2'>
                              <img
                                aria-label='Changed to'
                                src={'/images/arrow.png'}
                                style={{
                                  height: '16px',
                                  width: '16px',
                                  objectFit: 'cover',
                                  padding: '0px',
                                  alignSelf: 'center'
                                }}
                              />
                            </div>
                            <div className='flex items-center justify-center'>
                              {objMessage[key][field].note.__new !== null
                                ? objMessage[key][field].note.__new
                                : '(empty)'}
                            </div>
                          </div>
                        )
                      } else {
                        return (
                          <div className='flex flex-row h5 w100'>
                            <div className='flex items-center justify-center mr2'>
                              <span className='h5 mr2 bold font-black'>{getFieldName(field)}: </span>
                              {objMessage[key][field].__old !== null ? objMessage[key][field].__old : '(empty)'}{' '}
                            </div>
                            <div className='flex items-center justify-center mr2'>
                              <img
                                aria-label='Changed to'
                                src={'/images/arrow.png'}
                                style={{
                                  height: '16px',
                                  width: '16px',
                                  objectFit: 'cover',
                                  padding: '0px',
                                  alignSelf: 'center'
                                }}
                              />
                            </div>
                            <div className='flex items-center justify-center'>
                              {objMessage[key][field].__new !== null ? objMessage[key][field].__new : '(empty)'}
                            </div>
                          </div>
                        )
                      }
                    } else if (field === 'phones') {
                      if (objMessage[key][field][0][1].updatedAt) {
                        const tempDate: string = objMessage[key][field][0][1].updatedAt.__new
                        const tempDateArray = tempDate.split('T')

                        updateAtDisplay = tempDateArray[0] + ' ' + tempDateArray[1].slice(0, -5)
                      }
                      return (
                        objMessage[key][field][0][1].phoneNumber && (
                          <div className='flex flex-row h5 w100'>
                            <div className='flex items-center justify-center mr2'>
                              <span className='h5 mr2 bold font-black'>{getFieldName(field)}: </span>
                              {objMessage[key][field][0][1].phoneNumber.__old !== null &&
                              objMessage[key][field][0][1].phoneNumber.__old !== ''
                                ? objMessage[key][field][0][1].phoneNumber.__old
                                : '(empty)'}{' '}
                            </div>
                            <div className='flex items-center justify-center mr2'>
                              <img
                                aria-label='Changed to'
                                src={'/images/arrow.png'}
                                style={{
                                  height: '16px',
                                  width: '16px',
                                  objectFit: 'cover',
                                  padding: '0px',
                                  alignSelf: 'center'
                                }}
                              />
                            </div>
                            <div className='flex items-center justify-center'>
                              {objMessage[key][field][0][1].phoneNumber.__new !== null &&
                              objMessage[key][field][0][1].phoneNumber.__new !== ''
                                ? objMessage[key][field][0][1].phoneNumber.__new
                                : '(empty)'}
                            </div>
                          </div>
                        )
                      )
                    } else if (field === 'updatedAt') {
                      const tempDate: string = objMessage[key][field].__new
                      const tempDateArray = tempDate.split('T')

                      updateAtDisplay = tempDateArray[0] + ' ' + tempDateArray[1].slice(0, -5)
                      return
                    } else {
                      let message = ''

                      if (typeof objMessage[key][field] === 'string') {
                        message = objMessage[key][field]
                      }
                      return (
                        <div className='flex h5'>
                          {getFieldName(field)}: {message}
                        </div>
                      )
                    }
                  })
                } else if (key === 'recurring') {
                  updateAtDisplay =
                    'Pickup: ' +
                    objMessage[key].pickup.address +
                    '; ' +
                    'Dropoff: ' +
                    objMessage[key].dropoff.address +
                    '; ' +
                    'Schedule days: ' +
                    objMessage[key].scheduleDays +
                    '; ' +
                    'Ends: ' +
                    objMessage[key].ends +
                    '; ' +
                    'Notes: ' +
                    objMessage[key].notes

                  return <div className='flex h5'>{updateAtDisplay}</div>
                } else {
                  return
                }
                return temp.toString() !== '' ? (
                  <div className='w100' key={objMessage.riderId}>
                    <div className='h5 pb1 bold font-black'>Updated at {updateAtDisplay}</div>
                    {temp}
                  </div>
                ) : (
                  <div key={objMessage.riderId}>
                    <div className='flex h5'>No changes</div>
                  </div>
                )
              })}
            </div>
          )
        }
      }
    ],
    []
  )
  React.useEffect(() => {
    const params = {
      pageSize: 10,
      pageIndex: 0
    }
    fetchData(params)
  }, [])

  const fetchData = async ({ pageSize, pageIndex }: { pageSize: number; pageIndex: number }) => {
    const changelogs: any = await props.getRiderChangeLog({
      riderId: props.riderId,
      skip: pageSize * pageIndex,
      take: pageSize
    })
    setRiderChangeLog(changelogs.items)
    setRiderChangeLogCount(changelogs.count)
    setLoading(false)
  }

  const getFieldName = (field: string) => {
    const positions = []
    for (let i = 0; i < field.length; i++) {
      if (field[i].match(/[A-Z]/) !== null) {
        positions.push(i)
      }
    }
    let x = 0
    positions.map(i => {
      if (i !== 0) {
        field = field.replace(field.charAt(i + x), ' ' + field.charAt(i + x).toLowerCase())
        x = x + 1
      }
    })

    return field
  }

  const pageCount = Math.ceil(riderChangeLogCount / PAGE_SIZE)
  return (
    <FloatingForm fullHeight={true}>
      <div className='react-table w100'>
        <div className='font-grey2 bold h5 mb1'>Rider Change Log</div>
        {loading === true ? (
          <div className='p3 m3 flex items-center justify-center h5 font-grey2'>
            <MoonLoader sizeUnit={'px'} size={40} color={'#405264'} loading={true} />
          </div>
        ) : (
          <div className='mt1 border-top w100'>
            <Table
              columns={columns}
              data={riderChangeLog}
              fetchData={fetchData}
              loading={loading}
              pageCount={pageCount}
            />
          </div>
        )}
      </div>
    </FloatingForm>
  )
}



// WEBPACK FOOTER //
// ./src/components/riders/riderdetails/RiderChangeLog.tsx